import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, MaskedLine, FadeStagger, FadeItem } from "@/components/Motion";

const TEAM_COLORS = ["#0d9488", "#4361ee", "#f59c1a"];
const TEAM_PHOTOS = ["/founders/portrait-marta.webp", "/founders/portrait-sara.webp", "/founders/portrait-ruben.webp"];
const SCENES = [
  { src: "/founders/scene-1.webp", span: "lg:col-span-3", ratio: "aspect-[3/2]" },
  { src: "/founders/scene-4.webp", span: "lg:col-span-2", ratio: "aspect-[3/2] lg:aspect-square lg:object-[72%_50%]" },
  { src: "/founders/scene-2.webp", span: "lg:col-span-2", ratio: "aspect-[3/2] lg:aspect-square" },
  { src: "/founders/scene-3.webp", span: "lg:col-span-3", ratio: "aspect-[3/2]" },
];

const Nosotros = () => {
  const { t } = useLang();
  const n = t.nosotros;

  return (
    <div data-testid="nosotros-page" className="bg-[#FAFAFD] text-[#353535]">
      <Navbar />

      <section className="px-5 sm:px-8 pt-36 pb-16 sm:pt-44 sm:pb-20">
        <div className="mx-auto max-w-[1400px]">
          <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#4361ee] mb-6">
            <MaskedLine delay={0.1}>{n.kicker}</MaskedLine>
          </div>
          <h1 className="font-playfair leading-[1.05] text-[10vw] sm:text-[6.5vw] lg:text-[5vw] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>{n.heroTitle}</MaskedLine>
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl text-base lg:text-lg text-[#999999] font-jakarta leading-relaxed">{n.heroSub}</p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-16 sm:pb-20">
        <div className="mx-auto grid max-w-[1400px] gap-4 lg:grid-cols-5">
          {SCENES.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className={s.span}>
              <div className="group overflow-hidden rounded-3xl">
                <img src={s.src} alt="Equipo de Analytics 4 People en la oficina" data-testid={`nosotros-photo-${i}`} className={`${s.ratio} w-full object-cover transition-transform duration-700 group-hover:scale-105`} loading="lazy" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#4361ee] text-white px-5 sm:px-8 py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-white/60 mb-6">{n.missionKicker}</div>
            <p className="font-playfair text-3xl sm:text-5xl leading-tight max-w-4xl">{n.missionBig}</p>
            <p className="mt-8 text-white/75 font-jakarta max-w-2xl text-sm sm:text-base leading-relaxed">{n.missionBody}</p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#4361ee] mb-14">{t.master.valuesKicker}</div>
          </Reveal>
          <div className="grid gap-10 lg:grid-cols-2">
            {t.master.values.map((v, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border-l-2 border-[#4361ee] pl-6 sm:pl-10" data-testid={`nosotros-value-${i}`}>
                  <div className="font-jbmono text-xs text-[#999999] mb-2">{v.num}</div>
                  <h3 className="font-cabinet text-xl sm:text-2xl font-bold mb-3">{v.title}</h3>
                  <p className="text-sm sm:text-base text-[#999999] font-jakarta leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 sm:py-28 border-t border-black/10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#4361ee] mb-4">{n.teamKicker}</div>
            <h2 className="font-playfair text-3xl sm:text-5xl leading-tight mb-14">{n.teamTitle}</h2>
          </Reveal>
          <FadeStagger className="grid gap-5 md:grid-cols-3" delay={0.1}>
            {n.team.map((member, i) => (
              <FadeItem key={i}>
                <div data-testid={`nosotros-team-${i}`} className="h-full overflow-hidden rounded-2xl border border-black/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(67,97,238,0.10)]">
                  <div className="overflow-hidden">
                    <img src={TEAM_PHOTOS[i]} alt={member.name} className="aspect-[5/4] w-full object-cover object-center transition-transform duration-700 hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TEAM_COLORS[i] }} />
                      <div className="font-cabinet text-2xl font-bold">{member.name}</div>
                    </div>
                    <div className="mt-1 font-jbmono text-[10px] uppercase tracking-[0.2em]" style={{ color: TEAM_COLORS[i] }}>{member.role}</div>
                    <p className="mt-4 text-sm font-jakarta text-[#353535]/80 leading-relaxed">{member.exp}</p>
                    <p className="mt-4 border-t border-black/8 pt-4 text-sm font-jakarta text-[#999999] leading-relaxed">“{member.dream}”</p>
                  </div>
                </div>
              </FadeItem>
            ))}
          </FadeStagger>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-24 sm:pb-32">
        <Reveal className="mx-auto max-w-[1400px] rounded-3xl bg-gradient-to-br from-[#4361ee] to-[#3050d8] text-white p-10 sm:p-20 text-center overflow-hidden relative">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-white/10" />
          <h2 className="font-playfair text-3xl sm:text-5xl leading-tight">{n.ctaTitle}</h2>
          <p className="mt-5 text-white/80 font-jakarta max-w-xl mx-auto">{n.ctaBody}</p>
          <Link data-testid="nosotros-cta-contact" to="/contact" className="group mt-9 inline-flex items-center gap-2 rounded-full bg-white px-9 py-4 text-sm font-jakarta font-bold text-[#4361ee] transition-all duration-300 hover:gap-4">
            {t.common.contactCta} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
};

export default Nosotros;
